<template>
  <v-dialog v-model="showOptOutDialog" persistent max-width="600px">
    <template v-slot:activator="{ on }">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
         <v-chip
            text-color="white"
            :color="responseColor"
            small
            v-on="on"
            v-on:click="handleEmailClick"
            close
            :close-icon="actionIcon"
            @click:close="handleEmailClick"
          >
            <v-avatar>
              <refresh-spinner v-show="loading" v-model="loading"></refresh-spinner>
              <v-icon
                v-show="!loading"
                >{{ responseIcon }}</v-icon>
            </v-avatar>
            &nbsp;
            <slot></slot>
         </v-chip>
        </template>
        <span>{{ suppressionStatus }}</span>
        <br>
        <span>Click to {{ isSuppressed ? 'OPT-IN' : 'OPT-OUT' }} Email</span>
      </v-tooltip>
    </template>
    <v-card>
      <template v-if="!isSuppressed">
        <v-card-title>
          <span class="text-h5">Opt Out of future campaigns</span>
          <v-spacer></v-spacer>
          <refresh-spinner v-show="isSubmittingData" v-model="isSubmittingData"></refresh-spinner>
        </v-card-title>
        <v-card-text>
          <read-only-field label="Email" :value="value" :loading="loading"/>
          <v-switch v-model="optOutAllDealers">
            <template v-slot:label>
              Opt-Out Email From All Dealers
            </template>
          </v-switch>
          <v-select
            v-model="optOutReason"
            :items="optOutReasons"
            hint="Reason for OptOut"
            persistent-hint
          ></v-select>
          <v-text-field
            v-model="optOutReasonOther"
            v-show="optOutReasonIsOther"
            outlined
            label="Other - Please explain why customer Opted-Out"
            class="mt-6"
          >
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="resetOptOut">Cancel</v-btn>
          <v-btn
            color="blue darken-1"
            text
            :disabled="!canOptOut"
            @click="submitOptOut"
          >
            Opt-Out
          </v-btn>
        </v-card-actions>
      </template>
      <!-- IS SUPPRESSED -->
      <template v-else-if="canReverseOptOut">
        <v-card-title>
          <span class="text-h5">Opt-In email</span>
          <v-spacer></v-spacer>
          <refresh-spinner v-show="isSubmittingData" v-model="isSubmittingData"></refresh-spinner>
        </v-card-title>
        <v-card-text>
          <read-only-field label="Email" :value="value" :loading="loading"/>
       </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="resetOptOut">Cancel</v-btn>
          <v-btn
            color="blue darken-1"
            text
            @click="submitReverseOptOut"
          >
            Opt-IN
          </v-btn>
        </v-card-actions>
      </template>
      <!-- can not handle it - just show a message -->
      <template v-else>
        <v-card-title>
          <span class="text-h5">Unable to Opt-In</span>
          <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">
            This Email <strong>can not</strong> Opt-In this way because the email status was originally [{{suppressionReason}}].
          </p>
          <p class="text-body-1">
            Contact Support (doug@dealerapps.io) to discuss how to handle this.
          </p>
          <read-only-field label="Email" :value="value" :loading="loading"/>
       </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="resetOptOut">Close</v-btn>
        </v-card-actions>
      </template>

    </v-card>
  </v-dialog>
</template>

<script>
import { MailSuppressionApi } from '@/services'
import RefreshSpinner from '@/components/ui/RefreshSpinner'
import ReadOnlyField from '@/components/ui/ReadOnlyField'
import { mapState } from 'vuex'
import { hasValue } from '@/utils'
import { path } from 'ramda'

export default {
  name: 'email-suppression-chip',
  props: {
    value: {
      required: true
    }
  },
  components: {
    [ReadOnlyField.name]: ReadOnlyField,
    [RefreshSpinner.name]: RefreshSpinner
  },
  data: function () {
    return {
      loading: false,
      suppressionResponse: {},
      showOptOutDialog: false,

      optOutReason: '',
      optOutAllDealers: false,
      optOutDealerId: null,
      optOutReasons: [
        'Customer Emailed Dealer asking to be UnSubscribed',
        'Customer Called Dealer asking to be UnSubscribed',
        'Other'
      ],
      optOutReasonOther: null,
      isSubmittingData: false
    }
  },
  computed: {
    ...mapState('DealerStore', { dealer: 'dealer' }),
    ...mapState({
      accountKey: state => path(['attributes', 'custom:account_key'], state.cognito.user),
      currentUserEmail: state => path(['attributes', 'email'], state.cognito.user),
      currentUserName: state => path(['attributes', 'name'], state.cognito.user)
    }),
    email () {
      return this.suppressionResponse.email
    },
    suppressionReason () {
      return this.suppressionResponse.reason
    },
    isSuppressed () {
      return !!this.suppressionResponse.suppress
    },
    suppressionStatus () {
      if (this.isSuppressed) {
        return `${this.email} is suppressed because [${this.suppressionReason}].`
      } else {
        return `${this.email} is valid and not suppressed.`
      }
    },
    responseIcon () {
      if (this.loading) {
        return 'mdi-timer-sand-empty'
      } else {
        return this.isSuppressed ? 'mdi-email-off-outline' : 'mdi-email-outline'
      }
    },
    responseColor () {
      if (this.loading) {
        return 'grey'
      } else {
        return this.isSuppressed ? 'red' : 'green'
      }
    },
    actionIcon () {
      if (this.loading) {
        return 'mdi-timer-sand-empty'
      } else {
        return this.isSuppressed ? 'mdi-plus-outline' : 'mdi-close-outline'
      }
    },
    optOutReasonIsOther () {
      return this.optOutReason === 'Other'
    },
    canOptOut () {
      return !this.isSuppressed &&
        (
          (this.optOutReasonIsOther && hasValue(this.optOutReasonOther)) ||
          (!this.optOutReasonIsOther && hasValue(this.optOutReason))
        )
    },
    canReverseOptOut () {
      return this.isSuppressed && (
        this.suppressionReason.includes('Account-OptOut') ||
         this.suppressionReason.includes('Sender-OptOut')
      )
    }

  },
  methods: {
    checkSuppression (email) {
      this.$logger.trace('Checking Suppression for ', email)
      this.loading = true
      MailSuppressionApi.checkEmailSuppression([email], this.dealer.dms_key)
        .then(resp => {
          this.suppressionResponse = resp[0]
          this.loading = false
        })
        .catch(error => {
          this.$logger.error('unable to check suppression', error.message)
          this.loading = false
          this.$flash({
            showClose: true,
            message: 'unable to check suppression. ' + error.message,
            type: 'error',
            duration: 0
          })
        })
    },
    handleEmailClick (data) {
      if (this.loading) {
        return
      }
      this.showOptOutDialog = true
    },
    submitOptOut () {
      this.isSubmittingData = true

      const userPart = `ExecutedBy: ${this.currentUserName} [${this.currentUserEmail}]`
      const reason = this.optOutReasonIsOther
        ? `R4: ${this.optOutReason}: ${this.optOutReasonOther} - ${userPart}`
        : `R4: ${this.optOutReason} - ${userPart}`

      MailSuppressionApi.optOutEmail(
        this.value,
        reason,
        this.accountKey,
        this.optOutAllDealers ? null : this.dealer.dms_key,
        null
      )
        .then(resp => {
          this.isSubmittingData = false
          this.resetOptOut()
          this.checkSuppression(this.value)
        })
        .catch(error => {
          this.$logger.error('Unable to opt out customer.', error.message)
          this.isSubmittingData = false
          this.$flash({
            showClose: true,
            message: 'Unable to opt out customer. ' + error.message,
            type: 'error',
            duration: 0
          })
        })
    },
    submitReverseOptOut () {
      this.isSubmittingData = true

      MailSuppressionApi.reverseOptOutEmail(
        this.value,
        this.accountKey,
        this.suppressionReason === 'Sender-OptOut' ? this.dealer.dms_key : null
      )
        .then(resp => {
          this.isSubmittingData = false
          this.resetOptOut()
          this.checkSuppression(this.value)
        })
        .catch(error => {
          this.$logger.error('Unable to opt in customer.', error.message)
          this.isSubmittingData = false
          this.$flash({
            showClose: true,
            message: 'Unable to opt in customer. ' + error.message,
            type: 'error',
            duration: 0
          })
        })
    },
    resetOptOut () {
      this.showOptOutDialog = false
      this.optOutAllDealers = false
      this.optOutReason = null
      this.optOutDealerId = null
      this.optOutReasonOther = null
    }

  },
  mounted () {
    this.checkSuppression(this.value)
  }
}
</script>
